import { Chip, Typography, capitalize } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { format, isFuture, isBefore, isAfter } from "date-fns";
import { MoxieCardType } from "@/__generated__/schema.graphql.types";
import AdditionalInfo from "@/components/common/additionalInfo/additionalInfo";
import { CreditCardIcon } from "@/components/common/icons";
import { DATE_FORMATS } from "@/config";
import { ClientMembershipsQuery } from "@/graphql/queries/clientMemberships.graphql.types";
import { MembershipStatus } from "@/types";
import { formattedPrice } from "@/utils";
import {
  BillingFrequencyIntervals,
  getBillingFrequencyFromInterval,
} from "@/utils/memberships";

const formatMembershipDatePriceInfo = (
  startDate: string,
  pricePerBillingPeriod: string,
  billingInterval: BillingFrequencyIntervals,
  billingIntervalCount: number,
  cancelAt?: string
) => {
  const billingFrequency = getBillingFrequencyFromInterval(
    billingInterval as BillingFrequencyIntervals,
    billingIntervalCount
  );
  const billingFrequencyString = `${formattedPrice(
    pricePerBillingPeriod
  )} • billed ${billingFrequency.label}`;
  const start = new Date(startDate);
  const cancel = cancelAt ? new Date(cancelAt) : null;

  // Membership cancelled after starting
  if (cancel && isAfter(cancel, start)) {
    return `Member from ${format(start, DATE_FORMATS.DATE_PICKER)} to ${format(
      cancel,
      DATE_FORMATS.DATE_PICKER
    )}`;
  }

  // Membership canceled before it started
  if (cancel && isBefore(cancel, start)) {
    return `Canceled before starting • ${billingFrequencyString}`;
  }

  // Membership starting in future
  if (isFuture(start)) {
    return `Starting on ${format(
      new Date(startDate),
      DATE_FORMATS.DATE_PICKER
    )} • ${billingFrequencyString}`;
  }

  // Membership already started, but not cancelled
  return `Since ${format(
    start,
    DATE_FORMATS.DATE_PICKER
  )} • ${billingFrequencyString}`;
};

const getMembershipStatusLabel = (status: MembershipStatus) => {
  switch (status) {
    case MembershipStatus.ACTIVE:
      return "Active";
    case MembershipStatus.CANCELED:
      return "Cancelled";
    case MembershipStatus.PAST_DUE:
      return "Past due";
    case MembershipStatus.NOT_STARTED:
      return "Not started";
    default:
      return "";
  }
};

const MembershipStatusColors = {
  [MembershipStatus.CANCELED]: "yellow",
  [MembershipStatus.ACTIVE]: "green",
  [MembershipStatus.PAST_DUE]: "red",
  [MembershipStatus.NOT_STARTED]: "yellow",
};

type ClientMembershipType = ClientMembershipsQuery["clientMembership"][number];

export const MembershipClientCardContent = ({
  membership,
  defaultCard,
}: {
  membership: ClientMembershipType;
  defaultCard?: MoxieCardType;
}) => {
  const getMembershipCardRows = (membership: ClientMembershipType) => {
    const rows = [
      {
        id: 1,
        content: (
          <MembershipStatusChip
            status={membership.status as MembershipStatus}
          />
        ),
      },
      {
        id: 2,
        content: formatMembershipDatePriceInfo(
          membership.startedAt,
          membership.membership.pricePerBillingPeriod,
          membership.membership.billingInterval as BillingFrequencyIntervals,
          membership.membership.billingIntervalCount,
          membership.cancelAt
        ),
      },
    ];
    if (membership.status !== MembershipStatus.CANCELED && defaultCard) {
      rows.push({
        id: 3,
        content: (
          <CardLastNumbers
            status={membership.status as MembershipStatus}
            brand={defaultCard.brand}
            endCardNumbers={defaultCard.last4}
          />
        ),
      });
    }
    return rows;
  };

  const rows = getMembershipCardRows(membership);

  const showMembershipEndWarning =
    membership.status === MembershipStatus.ACTIVE && !!membership.cancelAt;

  return (
    <Grid
      container
      spacing={1}
      sx={{
        ml: 3,
        pr: 4,
      }}
    >
      {rows.map((row) => (
        <Grid key={row.id} size={12}>
          {typeof row.content === "string" ? (
            <Typography
              variant="paragraphSmall"
              sx={{
                color: "secondary.main",
              }}
            >
              {row.content}
            </Typography>
          ) : (
            row.content
          )}
        </Grid>
      ))}
      {showMembershipEndWarning && (
        <AdditionalInfo
          variant="warning"
          text={`Active member until ${format(
            new Date(membership.cancelAt),
            DATE_FORMATS.DATE_PICKER
          )}`}
        />
      )}
      {membership.status === MembershipStatus.PAST_DUE && (
        <AdditionalInfo
          variant="warning"
          text="This client has an overdue membership balance"
        />
      )}
    </Grid>
  );
};

const MembershipStatusChip = ({ status }: { status: MembershipStatus }) => {
  return (
    <Chip
      label={getMembershipStatusLabel(status)}
      color={MembershipStatusColors[status]}
    />
  );
};

const CardLastNumbers = ({
  status,
  brand,
  endCardNumbers,
}: {
  status: MembershipStatus;
  brand: string;
  endCardNumbers: string;
}) => {
  const color =
    status === MembershipStatus.PAST_DUE ? "error.main" : "text.secondary";

  return (
    <Typography
      component="div"
      sx={{
        display: "flex",
        gap: 1,
        color: color,
      }}
    >
      <CreditCardIcon color="currentColor" />
      <Typography variant="paragraphSmall">
        Using default card: {capitalize(brand || "")} x{endCardNumbers}
      </Typography>
    </Typography>
  );
};
